import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Copy, Mail } from 'lucide-react';
import { createInvite, getGroups, getRoles } from '../../services/iam';
import { CreateInviteResponse, Group, Role } from '../../types/iam';
import { toast } from '@/lib/toast';
import { copyWithToast } from '@/lib/copy';
import { Button, Checkbox, Combobox, Input, Modal, Select } from '@/components/ds-zta';
import { apiErrorMessage } from './iamForm';

interface Props {
  onClose: () => void;
}

// How long an invite stays redeemable. The backend caps it at 30 days.
const EXPIRY_OPTIONS = [
  { hours: 24, label: '1 day' },
  { hours: 72, label: '3 days' },
  { hours: 168, label: '7 days' },
  { hours: 336, label: '14 days' },
  { hours: 720, label: '30 days' },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Why the server sent no email — "you unticked it" and "this server cannot
// send mail" call for different things from the administrator.
const NO_EMAIL_REASON: Record<string, string> = {
  not_requested: 'No email was sent, because "Email the invitation" was not ticked.',
  not_configured: 'No email was sent: email delivery is not configured on this server (SMTP).',
  failed: 'The invitation email could not be delivered — see the admin-api log for the reason.',
};

// InviteUserModal records a pending membership for one email. Unlike Add User
// (which picks someone already in the d2bsuite directory and creates them now),
// an invite works for anyone: the account is created with the chosen role and
// groups the first time that person signs in with this exact, verified email.
const InviteUserModal: React.FC<Props> = ({ onClose }) => {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [groups, setGroups] = useState<string[]>([]);
  const [expiresInHours, setExpiresInHours] = useState(168);
  const [sendEmail, setSendEmail] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState<CreateInviteResponse | null>(null);

  const { data: rolesResponse } = useQuery(['roles', 'primary'], () => getRoles({ limit: 100, primary_only: true }));
  const roles: Role[] = rolesResponse?.data || [];
  const { data: groupsResponse } = useQuery(['groups', 'all'], () => getGroups({ limit: 200 }));
  const groupOptions = useMemo(
    () => (groupsResponse?.data || []).map((g: Group) => ({ value: g.id, label: g.name, hint: g.key })),
    [groupsResponse]
  );

  const trimmed = email.trim();
  const emailError = submitted && !EMAIL_PATTERN.test(trimmed) ? 'Enter a valid email address' : undefined;

  const mutation = useMutation(
    () =>
      createInvite({
        email: trimmed,
        roles: role ? [role] : [],
        groups,
        expires_in_hours: expiresInHours,
        send_email: sendEmail,
      }),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('invites');
        if (data.email_sent) {
          toast.success(`Invitation sent to ${data.invite.email}`);
          onClose();
          return;
        }
        // No email went out (not requested, or SMTP is not configured): keep the
        // modal open on the sign-in link so the admin can pass it on themselves.
        setResult(data);
      },
      onError: (err: unknown) => {
        const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
        if (code === 'ALREADY_MEMBER') toast.error(`${trimmed} is already a member of this workspace`);
        else if (code === 'INVITE_EXISTS') toast.error(`${trimmed} already has a pending invite`);
        else toast.error(`Failed to invite user: ${apiErrorMessage(err)}`);
      },
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (!EMAIL_PATTERN.test(trimmed)) return;
    mutation.mutate();
  };

  if (result) {
    return (
      <Modal
        open
        title="Invite created"
        onClose={onClose}
        footer={<Button onClick={onClose}>Done</Button>}
      >
        <div className="space-y-4 text-sm text-[var(--d2d-text-secondary)]">
          <p>
            <span className="font-medium text-[var(--d2d-text-primary)]">{result.invite.email}</span> can join this
            workspace by signing in with that email address.{' '}
            {NO_EMAIL_REASON[result.email_status ?? ''] ?? 'No email was sent.'} Share this sign-in link with them:
          </p>
          <Input
            label="Sign-in link"
            readOnly
            value={result.sign_in_url}
            onFocus={(e) => e.currentTarget.select()}
            trailingSlot={
              <Button
                size="sm"
                variant="secondary"
                leadingIcon={<Copy className="h-4 w-4" />}
                onClick={() => copyWithToast(result.sign_in_url, { success: 'Sign-in link copied' })}
              >
                Copy
              </Button>
            }
          />
          <p className="text-xs text-[var(--d2d-text-muted)]">
            The link itself grants nothing: only someone who signs in as {result.invite.email} joins.
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open
      title="Invite User"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="invite-user-form"
            loading={mutation.isLoading}
            leadingIcon={mutation.isLoading ? undefined : <Mail className="h-4 w-4" />}
            disabled={mutation.isLoading || !trimmed}
          >
            {mutation.isLoading ? 'Inviting…' : 'Send Invite'}
          </Button>
        </>
      }
    >
      <form id="invite-user-form" onSubmit={handleSubmit} className="space-y-5 text-[var(--d2d-text-primary)]">
        <p className="-mt-2 text-sm text-[var(--d2d-text-muted)]">
          Invite someone by email. Their account is created with the role and groups below the first time they sign
          in with this address.
        </p>

        <Input
          label="Email"
          type="email"
          required
          autoFocus
          placeholder="name@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={emailError}
        />

        <Select id="invite_role" label="Role" value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="">Viewer (default)</option>
          {roles.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </Select>

        {groupOptions.length > 0 && (
          <Combobox
            multiple
            label="Groups"
            placeholder="Add to groups (optional)…"
            options={groupOptions}
            value={groups}
            onChange={setGroups}
          />
        )}

        <Select
          id="invite_expiry"
          label="Invite expires after"
          value={String(expiresInHours)}
          onChange={(e) => setExpiresInHours(Number(e.target.value))}
        >
          {EXPIRY_OPTIONS.map((o) => (
            <option key={o.hours} value={o.hours}>
              {o.label}
            </option>
          ))}
        </Select>

        <Checkbox
          label="Email the invitation"
          description="Sent only when email delivery is configured; otherwise you get a sign-in link to share."
          checked={sendEmail}
          onChange={setSendEmail}
        />
      </form>
    </Modal>
  );
};

export default InviteUserModal;
